import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MessageCircle, MapPin, Send, Clock, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import UrgentCarePointerBlock from '../components/UrgentCarePointerBlock';
import FloatingChat from '../components/FloatingChat';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { submitContactForm } from '../lib/api';
import { useContactInfo, getWhatsAppHref, getTelHref } from '../contexts/ContactInfoContext';
import { COMPANY_DETAILS } from '../data/company';
import { PUBLIC_PAGE_SEO } from '../data/publicPageSeo';

const ContactPage = () => {
  const { contactInfo } = useContactInfo();
  // Keep API details when present; use the same verified Vietnam contact
  // already published in Company Details and on the urgent-care contact block.
  const phone = contactInfo?.hotlinePhone?.trim() || COMPANY_DETAILS.phones[1];
  const contacts = {
    hotlinePhone: phone,
    hotlineDisplay: contactInfo?.hotlineDisplay?.trim() || phone,
    whatsappNumber: contactInfo?.whatsappNumber?.trim() || COMPANY_DETAILS.phones[1],
    contactEmail: contactInfo?.contactEmail?.trim() || COMPANY_DETAILS.emails[1],
  };
  const whatsappHref = getWhatsAppHref(contacts, 'Hi VietClinics, I need help with...');
  const telHref = getTelHref(contacts);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    try {
      await submitContactForm({
        name: formData.name,
        email: formData.email,
        subject: formData.subject || undefined,
        message: formData.message,
      });
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{PUBLIC_PAGE_SEO.contact.title}</title>
        <meta name="description" content={PUBLIC_PAGE_SEO.contact.description} />
        <link rel="canonical" href="https://vietclinics.com/contact" />
      </Helmet>
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-r from-[#1E5BA8] to-[#1FA7A1] py-12">
        <div className="container mx-auto px-4 text-center">
          <MessageCircle className="h-12 w-12 text-white mx-auto mb-4" />
          <h1 data-testid="contact-title" className="text-3xl md:text-4xl font-bold text-white mb-3">
            Contact & Support
          </h1>
          <p className="text-white/80 max-w-xl mx-auto">
            We're here to help. Reach out to us through any of the channels below.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          
          {/* Contact Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {/* WhatsApp */}
            {whatsappHref && (
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-[#25D366]"
                data-testid="contact-whatsapp-card"
              >
                <div className="w-14 h-14 bg-[#25D366]/10 rounded-xl flex items-center justify-center mb-4">
                  <MessageCircle className="h-7 w-7 text-[#25D366]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">WhatsApp</h3>
                <p className="text-[#25D366] font-medium mb-2 break-words">{contacts.whatsappNumber}</p>
                <p className="text-sm text-gray-500">Message our coordination team</p>
              </a>
            )}

            {/* Email */}
            {contacts.contactEmail && (
              <a
                href={`mailto:${contacts.contactEmail}`}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow border-2 border-transparent hover:border-[#1FA7A1]"
                data-testid="contact-email-card"
              >
                <div className="w-14 h-14 bg-[#1FA7A1]/10 rounded-xl flex items-center justify-center mb-4">
                  <Mail className="h-7 w-7 text-[#1FA7A1]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">Email</h3>
                <p className="text-[#1FA7A1] font-medium mb-2 break-words">{contacts.contactEmail}</p>
                <p className="text-sm text-gray-500">Send your enquiry by email</p>
              </a>
            )}

            {/* Phone */}
            {telHref && (
              <a
                href={telHref}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow border-2 border-transparent hover:border-[#1E88E5]"
                data-testid="contact-phone-card"
              >
                <div className="w-14 h-14 bg-[#1E88E5]/10 rounded-xl flex items-center justify-center mb-4">
                  <Phone className="h-7 w-7 text-[#1E88E5]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">Phone</h3>
                <p className="text-[#1E88E5] font-medium mb-2 break-words">{contacts.hotlineDisplay}</p>
                <p className="text-sm text-gray-500">Call about non-emergency coordination</p>
              </a>
            )}
          </div>

          <section className="mb-10" aria-label="VietClinics chat" data-testid="contact-chat-section">
            <FloatingChat embedded />
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm" data-testid="contact-form-panel">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Send us a Message</h2>
              
              {isSubmitted ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <div className="w-16 h-16 bg-[#2ECC71]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-[#2ECC71]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                  <p className="text-gray-600 mb-6">
                    Thank you for contacting us. We'll get back to you within 24 hours.
                  </p>
                  <Button 
                    onClick={() => {
                      setIsSubmitted(false);
                      setFormData({ name: '', email: '', subject: '', message: '' });
                    }}
                    variant="outline"
                    data-testid="contact-send-another"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Your Name *
                    </label>
                    <Input
                      type="text"
                      placeholder="John Doe"
                      data-testid="contact-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="h-11"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      data-testid="contact-email-input"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="h-11"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject
                    </label>
                    <select
                      data-testid="contact-subject-select"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      className="w-full h-11 px-3 rounded-lg border border-gray-200 bg-white"
                    >
                      <option value="">Select a topic</option>
                      <option value="booking">Booking Help</option>
                      <option value="clinic">Clinic Registration</option>
                      <option value="payment">Payment Issue</option>
                      <option value="refund">Refund Request</option>
                      <option value="feedback">Feedback</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message *
                    </label>
                    <textarea
                      placeholder="How can we help you?"
                      data-testid="contact-message-input"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full p-3 border border-gray-200 rounded-lg resize-none h-32 focus:outline-none focus:ring-2 focus:ring-[#1FA7A1]/50"
                      required
                    />
                  </div>

                  <Button 
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={isSubmitting}
                    className="w-full bg-[#1FA7A1] hover:bg-[#178f8a] py-5"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Info Section */}
            <div className="space-y-6">
              {/* Office Location */}
              {contactInfo?.officeAddress && (
                <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="contact-office-card">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-[#1FA7A1]" />
                    Our Office
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    VietClinics Headquarters<br />
                    {contactInfo.officeAddress}
                  </p>
                </div>
              )}

              {/* Support Hours */}
              <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="contact-availability-note">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-[#1FA7A1]" />
                  Response & Availability
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Response times depend on your enquiry. Appointments and services
                  are subject to provider review and availability. VietClinics is
                  not an emergency service.
                </p>
              </div>

              {/* Quick Help */}
              <div className="bg-[#1FA7A1]/10 rounded-2xl p-6" data-testid="contact-quick-help">
                <h3 className="font-bold text-gray-900 mb-3">Need Quick Help?</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Check our FAQ section for answers to common questions about bookings, 
                  payments, and clinic registration.
                </p>
                <a href="/faq" data-testid="contact-faq-link">
                  <Button variant="outline" className="w-full" data-testid="contact-faq-button">
                    View FAQ
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <UrgentCarePointerBlock testId="contact-urgent-pointer" />

      <Footer />
    </div>
  );
};

export default ContactPage;
